import React, { useEffect, useState } from "react";
import { Button, InputNumber, Table, Tag, message } from "antd";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import authApi from "../../../api/authApi";

const CAMP_COLORS = { shopee: "orange", lazada: "blue", tiktok: "default" };

/** Convert raw decimal rates to display percentages */
function toDisplayPercents(el) {
    const ur = Number(el.userRate) || 0;
    const sr = el.sellerUserRate != null && el.sellerUserRate !== "" ? Number(el.sellerUserRate) : ur;
    const br = el.baseUserRate   != null && el.baseUserRate   !== "" ? Number(el.baseUserRate)   : ur;
    return { ...el, userRate: ur * 100, sellerUserRate: sr * 100, baseUserRate: br * 100 };
}

function Commission() {
    const [campaigns, setCampaigns] = useState([]);
    const [isEditing, setIsEditing]   = useState(false);
    const [loading, setLoading]       = useState(false);
    const [saving, setSaving]         = useState(false);

    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            const { data } = await authApi.getCampaign();
            setCampaigns(data.campaigns.map(toDisplayPercents));
        } catch {
            message.error("Không thể tải dữ liệu chiến dịch");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCampaigns(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onSave = async () => {
        setSaving(true);
        try {
            const campaignsToUpdate = campaigns.map((el) => {
                // Destructure out X2 fields (moved to Chiến dịch X2 page) so they
                // are never written back by this form.
                // eslint-disable-next-line no-unused-vars
                const { appRateEnabled, appMultiplier, appRateStart, appRateEnd, ...rest } = el;
                return {
                    ...rest,
                    userRate:       Number(el.baseUserRate)   / 100,
                    sellerUserRate: Number(el.sellerUserRate) / 100,
                    baseUserRate:   Number(el.baseUserRate)   / 100,
                };
            });
            await authApi.updateCampaign({ campaignList: campaignsToUpdate });
            message.success("Cập nhật thành công");
            setIsEditing(false);
            fetchCampaigns();
        } catch {
            message.error("Cập nhật thất bại");
        } finally {
            setSaving(false);
        }
    };

    const onCancel = () => { setIsEditing(false); fetchCampaigns(); };

    const setField = (campId, field, value) =>
        setCampaigns((prev) =>
            prev.map((el) => el.campId !== campId ? el : { ...el, [field]: value })
        );

    const columns = [
        {
            title: "Kênh", dataIndex: "campId", key: "campId", width: 110,
            render: (v) => <Tag color={CAMP_COLORS[v] || "default"}>{v?.toUpperCase()}</Tag>,
        },
        {
            title: "% Seller (Xtra)", dataIndex: "sellerUserRate", key: "sellerUserRate",
            width: 160, align: "center",
            render: (v, r) => isEditing
                ? <InputNumber
                    size="small"
                    min={0} max={100} step={0.1}
                    value={v}
                    onChange={(val) => setField(r.campId, "sellerUserRate", val)}
                    addonAfter="%"
                    style={{ width: 120 }}
                  />
                : <span className="font-semibold">{v} %</span>,
        },
        {
            title: "% Base", dataIndex: "baseUserRate", key: "baseUserRate",
            width: 160, align: "center",
            render: (v, r) => isEditing
                ? <InputNumber
                    size="small"
                    min={0} max={100} step={0.1}
                    value={v}
                    onChange={(val) => setField(r.campId, "baseUserRate", val)}
                    addonAfter="%"
                    style={{ width: 120 }}
                  />
                : <span className="font-semibold">{v} %</span>,
        },
        // Cột "% User (legacy)" đã bỏ khỏi bảng (28/07): giá trị luôn được ghi bằng
        // % Base khi lưu, chỉ còn là fallback cho đơn cũ chưa có rate tách phần.
    ];

    return (
        <div className="flex-1 flex flex-col p-5 overflow-auto gap-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold">Cài đặt hoa hồng</h1>
                    <p className="text-xs text-blue-500 mt-0.5">
                        Đợt nhân hoa hồng (X2/X3) cấu hình ở{" "}
                        <Link to="/reward-program" className="underline font-medium">
                            Chương trình thưởng
                        </Link>
                        . Lưu ý: hệ số nhân áp trên các % dưới đây và bị chặn ở 100% mỗi phần
                        (Xtra 40% ×2 = 80%, Base 50% ×2 = 100%) nên tiền trả user không vượt hoa hồng sàn trả.
                    </p>
                </div>
                <div className="flex gap-2">
                    {!isEditing && (
                        <Button
                            type="primary"
                            icon={<FontAwesomeIcon icon={faPenToSquare} />}
                            onClick={() => setIsEditing(true)}
                        >
                            Chỉnh sửa
                        </Button>
                    )}
                    {isEditing && (
                        <>
                            <Button
                                type="primary"
                                icon={<FontAwesomeIcon icon={faCheck} />}
                                loading={saving}
                                onClick={onSave}
                            >
                                Lưu
                            </Button>
                            <Button
                                icon={<FontAwesomeIcon icon={faXmark} />}
                                onClick={onCancel}
                                disabled={saving}
                            >
                                Huỷ
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <p className="text-sm text-content-3 -mt-2">
                % Seller (Xtra) áp trên phần hoa hồng seller; % Base áp trên hoa hồng nền tảng.
                Kênh chỉ có một pool (Lazada, TikTok) dùng % Base.
            </p>

            <div className="bg-surface border border-line rounded-lg shadow-sm">
                <Table
                    size="small"
                    dataSource={campaigns}
                    rowKey="campId"
                    columns={columns}
                    pagination={false}
                    loading={loading}
                    scroll={{ x: "max-content" }}
                />
            </div>
        </div>
    );
}

export default Commission;
